/**
 * CampaignBanner Component
 * Reusable promotional banner for campaigns
 * Example: "Welcome Back To School" with product showcase
 */

import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CampaignProduct {
  id: string;
  name: string;
  price: number;
  image: string;
  badge?: string;
}

interface CampaignBannerProps {
  title: string;
  subtitle?: string;
  products: CampaignProduct[];
  backgroundColor?: string;
  mascotImage?: string;
  campaignUrl?: string;
  className?: string;
}

const CampaignBanner = ({
  title,
  subtitle,
  products,
  backgroundColor = "from-blue-600 via-purple-600 to-pink-600",
  mascotImage,
  campaignUrl = "/shop",
  className,
}: CampaignBannerProps) => {
  return (
    <section className={cn("relative overflow-hidden", className)}>
      <div className={cn("bg-gradient-to-r", backgroundColor)}>
        <div className="container mx-auto px-4 py-8 md:py-12">
          <div className="grid lg:grid-cols-12 gap-6 items-center">
            {/* Left - Campaign Info */}
            <div className="lg:col-span-3 text-white space-y-4">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-2">
                  {title}
                </h2>
                {subtitle && (
                  <p className="text-white/90 text-lg">
                    {subtitle}
                  </p>
                )}
              </div>

              <Button
                asChild
                size="lg"
                className="bg-white text-gray-900 hover:bg-gray-100 font-bold shadow-xl"
              >
                <Link to={campaignUrl}>
                  Shop Now
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>

              {/* Mascot Image */}
              {mascotImage && (
                <div className="hidden lg:block mt-6">
                  <img
                    src={mascotImage}
                    alt="Campaign mascot"
                    className="w-32 h-32 object-contain drop-shadow-2xl animate-bounce"
                  />
                </div>
              )}
            </div>

            {/* Right - Products Showcase */}
            <div className="lg:col-span-9">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {products.map((product) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.id}`}
                    className="group relative bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:scale-105"
                  >
                    {/* Product Image */}
                    <div className="aspect-square relative bg-gray-100">
                      <img
                        src={product.image}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                      />

                      {/* Price Badge */}
                      <div className="absolute top-2 right-2 bg-yellow-400 text-gray-900 font-bold text-sm px-3 py-1 rounded-full shadow-lg transform rotate-12">
                        {product.price.toFixed(3)} BD
                      </div>

                      {/* Custom Badge */}
                      {product.badge && (
                        <div className="absolute top-2 left-2 bg-red-500 text-white font-bold text-xs px-2 py-1 rounded-full">
                          {product.badge}
                        </div>
                      )}
                    </div>

                    {/* Product Name */}
                    <div className="p-3 bg-white">
                      <h3 className="text-sm font-semibold text-gray-900 truncate">
                        {product.name}
                      </h3>
                    </div>

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-4">
                      <span className="text-white font-bold text-sm">
                        View Details
                      </span>
                    </div>
                  </Link>
                ))}
              </div>

              {/* More Products Indicator */}
              {products.length > 5 && (
                <div className="mt-4 text-center">
                  <Link
                    to={campaignUrl}
                    className="text-white hover:text-white/80 font-semibold inline-flex items-center gap-2"
                  >
                    View All Products
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
};

export default CampaignBanner;

/**
 * Example Usage:
 *
 * const backToSchoolProducts = [
 *   { id: '1', name: 'Math Notebook', price: 0.150, image: '/images/math-nb.jpg', badge: 'NEW' },
 *   { id: '2', name: 'Arabic Notebook', price: 0.150, image: '/images/arabic-nb.jpg' },
 *   { id: '3', name: 'English Notebook', price: 0.150, image: '/images/english-nb.jpg' },
 *   { id: '4', name: 'Office Files', price: 0.250, image: '/images/files.jpg' },
 *   { id: '5', name: 'Rulers Set', price: 0.500, image: '/images/rulers.jpg' },
 * ];
 *
 * <CampaignBanner
 *   title="Welcome Back To School"
 *   subtitle="Essential supplies starting at 0.150 BD"
 *   products={backToSchoolProducts}
 *   backgroundColor="from-blue-600 via-purple-600 to-pink-600"
 *   mascotImage="/images/school-mascot.png"
 *   campaignUrl="/campaigns/back-to-school"
 * />
 */